import { useEffect, useRef } from 'react';
import { useFarmScope } from '@/stores/farmScope';

interface StoreOption {
  id: string;
  farmId: string | null;
}

/**
 * Keeps a store-picking form field in step with the farms selected in the
 * top bar.
 *
 * When exactly one farm is selected, the field is narrowed to that farm's own
 * stores and defaulted to the first one — receiving stock while looking at
 * Sahiwal Farm 7 should not require finding F-07's store in a list mixed with
 * every other farm's. With several farms selected, or "All farms", the field
 * is narrowed to those farms' stores (or left unfiltered for "All farms") but
 * nothing is forced — a guessed store on a shipment is a real mistake to undo.
 *
 * The default only overwrites the field when the farm actually changes, or
 * when the current value has fallen outside the narrowed list (the farm
 * changed under it, or the store list loaded after the farm was already
 * selected) — never on every render, and never overriding a deliberate choice
 * the user already made within the same farm.
 */
export function useFarmScopedStore<T extends StoreOption>(
  stores: T[] | undefined,
  storeId: string,
  setStoreId: (id: string) => void,
) {
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const farmKey = farmIds.join(',');
  const singleFarmId = farmIds.length === 1 ? farmIds[0] : null;
  const prevFarmKey = useRef('');
  const hasSeenStores = useRef(false);

  const options = farmIds.length
    ? (stores ?? []).filter((s) => s.farmId !== null && farmIds.includes(s.farmId))
    : (stores ?? []);

  useEffect(() => {
    if (!stores) return;

    const farmChanged = prevFarmKey.current !== farmKey;
    prevFarmKey.current = farmKey;

    if (!singleFarmId) {
      hasSeenStores.current = true;
      return; // "All farms" or several at once: never force a choice.
    }

    const stillValid = options.some((s) => s.id === storeId);

    // Reset on a genuine farm change after the first load, or whenever the
    // current value is not one of this farm's stores (including the empty
    // value on a cold load). A valid store already in the field on first load
    // — one prefilled by "Receive more" from Current Stock — is kept.
    if ((farmChanged && hasSeenStores.current) || !stillValid) {
      setStoreId(options[0]?.id ?? '');
    }
    hasSeenStores.current = true;
    // options is derived from stores + farmKey, both already covered below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmKey, stores]);

  return { farmId: singleFarmId, options };
}
